import { FC, ReactNode, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { ChangeHistory } from '@api/models/common/ChangeHistory';
import { QcsTabs } from '../basic/QcsTabs';
import { ChangeHistoryPanel, ChangeHistorySource } from './ChangeHistoryPanel';
import { useCanSeeChangeHistory } from './useCanSeeChangeHistory';

const DETAIL_TAB = 'detail';
const HISTORY_TAB = 'history';

interface ExtraTab {
  id: string;
  label: string;
  content: ReactNode;
}

interface Props {
  /** The `changes` of the loaded detail; without it (a new entity) there is no history tab. */
  history?: ChangeHistory;
  /** The service that reads the entity detail (and with it the history), and the id of the entity. */
  service: ChangeHistorySource;
  id: string;
  /** Further tabs between the detail and the history; with any of them the tabs show even without the history tab. */
  extraTabs?: ExtraTab[];
  /** The detail itself, shown under the "Detail" tab. */
  children: ReactNode;
}

/**
 * Puts the entity detail and its change history (for the roles allowed to see it, and only for a saved entity) under tabs.
 * The open tab is the last segment of the URL (`/employee/<id>/history`; the detail has none), so a reload or a link keeps it.
 * The route of a screen using this declares that segment as `:tabId?`, or `history` as a static segment where `:tabId` is taken.
 * Every tab stays mounted while another is open, so an unsaved form is not lost by a peek at the history.
 */
export const ChangeHistoryTabs: FC<Props> = ({ history, service, id, extraTabs = [], children }) => {
  const { t } = useTranslation();
  const canSeeHistory = useCanSeeChangeHistory();
  const navigate = useNavigate();
  const { pathname, search, state } = useLocation();
  const { tabId } = useParams();

  // `:tabId` may also belong to the page this is embedded in (a drawer on an invitation tab), so only a tab of ours counts.
  const path = pathname.replace(/\/$/, '');
  const urlSegment = tabId ?? path.slice(path.lastIndexOf('/') + 1);
  const basePath = tabId || urlSegment === HISTORY_TAB ? path.slice(0, -(urlSegment.length + 1)) : path;
  const goTo = (id: string) => navigate({ pathname: id === DETAIL_TAB ? basePath : `${basePath}/${id}`, search }, { state });

  const showHistory = canSeeHistory && !!history;
  const tabs = [
    { id: DETAIL_TAB, label: t('changeHistory.tabs.detail'), content: children },
    ...extraTabs,
    ...(showHistory
      ? [
          {
            id: HISTORY_TAB,
            label: t('changeHistory.tabs.history'),
            content: <ChangeHistoryPanel history={history} service={service} id={id} />,
          },
        ]
      : []),
  ];

  // A link to the history for a role that cannot see it (the history itself may still be loading, so only the role is judged).
  if (urlSegment === HISTORY_TAB && !canSeeHistory) return <Navigate to={{ pathname: basePath, search }} state={state} replace />;
  if (tabs.length === 1) return <>{children}</>;

  const activeTab = tabs.find(({ id }) => id === urlSegment)?.id ?? DETAIL_TAB;

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <QcsTabs tabs={tabs} value={activeTab} onChange={(_event: SyntheticEvent, id: string) => goTo(id)} />
      </Box>
      {tabs.map(({ id, content }) => (
        <Box key={id} hidden={activeTab !== id}>
          {content}
        </Box>
      ))}
    </>
  );
};
